import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add, SyncAlt } from "@mui/icons-material";
import { FormSectionPane } from "../../../toolympus/components/primitives/PaneContainer";
import { TableDisplay } from "../../../toolympus/components/schemed/TableDisplay";
import { CopTreeNodeSchema } from "./useCopTreeNodes";
import {
  CopTreeNodeLinkSchema,
  useCopTreeNodeNodeLinks,
} from "./useCopTreeNodeNodeLinks";
import { AddNodeNodeLinkDialog } from "./AddNodeNodeLinkDialog";

interface Props {
  nodeId: number;
  disabled?: boolean;
}

export const CopTreeNodeLinksPanel: React.FC<Props> = ({
  nodeId,
  disabled = false,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    isLoading,
    allNodes,
    rootNodes,
    nodesById,
    linkedNodes,
    linkedNodeIds,
    addLink,
    removeLink,
  } = useCopTreeNodeNodeLinks(nodeId);

  const getNodePathBreadcrumb = (path?: string | null) => {
    if (!path) return "Root";
    return path
      .split(".")
      .map(id => nodesById.get(Number(id))?.title || id)
      .join(" / ");
  };

  return (
    <FormSectionPane
      titleIcon={<SyncAlt fontSize="small" color="primary" />}
      title={
        <FormattedMessage
          id="cop_tree.related_nodes"
          defaultMessage="Related Tree Nodes"
        />
      }
      rightControls={
        <>
          {isLoading && <CircularProgress size={14} />}
          {!disabled && (
            <Button
              size="small"
              color="primary"
              startIcon={<Add />}
              onClick={() => setIsDialogOpen(true)}
            >
              <FormattedMessage id="common.add" defaultMessage="Add Link" />
            </Button>
          )}
        </>
      }
    >
      {/* Chips Container */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          p: 1.5,
          minHeight: 48,
          alignItems: "center",
        }}
      >
        {linkedNodes.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage
              id="cop_tree.no_linked_nodes"
              defaultMessage="No related tree nodes linked yet."
            />
          </Typography>
        ) : (
          linkedNodes.map(({ link, otherNode, isOutgoing }) => (
            <Tooltip
              key={link._id}
              title={
                <Box>
                  <Typography variant="caption" display="block">
                    Path: {getNodePathBreadcrumb(otherNode.path)}
                  </Typography>
                  {link.description && (
                    <Typography variant="caption" display="block" sx={{ fontStyle: "italic", mt: 0.5 }}>
                      {link.description}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Chip
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {/* Relation Type Badge */}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "primary.main",
                        textTransform: "uppercase",
                        fontSize: "0.65rem",
                      }}
                    >
                      <TableDisplay
                        field="relation_type"
                        row={link}
                        schema={CopTreeNodeLinkSchema.relation_type}
                      />
                      {isOutgoing ? " →" : " ←"}
                    </Typography>

                    {/* Node Color */}
                    {otherNode.color && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: otherNode.color,
                        }}
                      />
                    )}

                    {/* Node Title */}
                    <span>{otherNode.title}</span>

                    {/* Node Type */}
                    {otherNode.type && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ opacity: 0.7, fontSize: "0.65rem" }}
                      >
                        (
                        <TableDisplay
                          field="type"
                          row={otherNode}
                          schema={CopTreeNodeSchema.type}
                        />
                        )
                      </Typography>
                    )}
                  </Box>
                }
                onDelete={disabled ? undefined : () => removeLink(link._id)}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                }}
              />
            </Tooltip>
          ))
        )}
      </Box>

      {/* Add Link Dialog */}
      <AddNodeNodeLinkDialog
        isOpen={isDialogOpen}
        close={() => setIsDialogOpen(false)}
        currentNodeId={nodeId}
        rootNodes={rootNodes}
        allNodes={allNodes}
        nodesById={nodesById}
        linkedNodeIds={linkedNodeIds}
        onAddLink={addLink}
      />
    </FormSectionPane>
  );
};